import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  CreditCard, 
  Package, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Upload,
  BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Sector } from 'recharts';

// Renders the hovered pie slice as a popped-out, ringed sector (instead of a box).
const renderActiveSlice = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8}
        startAngle={startAngle} endAngle={endAngle} fill={fill} stroke="#fff" strokeWidth={2} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 11} outerRadius={outerRadius + 14}
        startAngle={startAngle} endAngle={endAngle} fill={fill} opacity={0.45} />
    </g>
  );
};
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, getDashboardStats } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedAccess from '../../components/RoleBasedAccess';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    summary: {
      totalChargebacks: 0,
      totalBatches: 0,
      totalCustomers: 0,
      totalProducts: 0,
      totalContracts: 0
    },
    recentActivity: {
      chargebacks: [],
      batches: []
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which pie slice is hovered (for the pop-out highlight).
  const [activeCbSlice, setActiveCbSlice] = useState(undefined);
  const [activeBatchSlice, setActiveBatchSlice] = useState(undefined);
  
  console.log('🏠 Dashboard component rendering...');
  console.log('👤 User:', user);
  console.log('🔑 User roles:', user?.roles);
  console.log('🔍 Has ADMIN role:', hasRole(['ADMIN']));
  console.log('🔍 Has STAFF role:', hasRole(['STAFF']));
  
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log('📊 Fetching dashboard data...');
        setIsLoading(true);
        setError(null);
        
        const data = await getDashboardStats();
        console.log('📊 Dashboard data received:', data);
        
        setDashboardData(data);
      } catch (err) {
        console.error('❌ Error fetching dashboard data:', err);
        setError(err.message || 'Failed to fetch dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);
  
  // Early return for debugging
  if (!user) {
    console.log('❌ No user found, showing loading...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No User Found</h1>
          <p className="text-gray-600">Please log in to access the dashboard.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const stats = [
    // Admin & Staff Stats
    ...(hasRole(['ADMIN', 'STAFF']) ? [
      {
        name: 'Total Customers',
        value: dashboardData?.summary?.totalCustomers || 0,
        icon: Users,
        color: 'bg-blue-500',
        change: '+12%',
        changeType: 'positive',
        roles: ['ADMIN', 'STAFF']
      },
      {
        name: 'Active Contracts',
        value: dashboardData?.summary?.totalContracts || 0,
        icon: FileText,
        color: 'bg-green-500',
        change: '+8%',
        changeType: 'positive',
        roles: ['ADMIN', 'STAFF']
      },
      {
        name: 'Total Products',
        value: dashboardData?.summary?.totalProducts || 0,
        icon: Package,
        color: 'bg-indigo-500',
        change: '+20%',
        changeType: 'positive',
        roles: ['ADMIN', 'STAFF']
      },
      {
        name: 'Active Batches',
        value: dashboardData?.summary?.totalBatches || 0,
        icon: TrendingUp,
        color: 'bg-orange-500',
        change: '+5%',
        changeType: 'positive',
        roles: ['ADMIN', 'STAFF']
      }
    ] : []),
    // All Users Stats
    {
      name: 'Total Chargebacks',
      value: dashboardData?.summary?.totalChargebacks || 0,
      icon: CreditCard,
      color: 'bg-purple-500',
      change: '+15%',
      changeType: 'positive',
      roles: ['ADMIN', 'STAFF', 'CUSTOMER']
    }
  ];

  const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    VALIDATED: 'bg-green-100 text-green-800',
    ERROR: 'bg-red-100 text-red-800',
    POSTED: 'bg-blue-100 text-blue-800'
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4" />;
      case 'VALIDATED':
        return <CheckCircle className="h-4 w-4" />;
      case 'ERROR':
        return <AlertTriangle className="h-4 w-4" />;
      case 'POSTED':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-800 via-primary-600 to-accent-600 p-6 sm:p-8 text-white shadow-card">
        <div className="absolute -right-10 -top-12 h-44 w-44 rounded-full bg-white/10" />
        <div className="absolute right-16 bottom-0 h-24 w-24 rounded-full bg-white/5" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-primary-100">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold">
              Welcome back{user?.name ? `, ${String(user.name).split(' ')[0]}` : ''} 👋
            </h1>
            <p className="mt-1 text-sm text-primary-100">Here's what's happening with your chargeback operations.</p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" /> All systems operational
            </span>
            <div className="flex flex-wrap gap-1.5">
              {user?.roles?.map((role, index) => (
                <span key={index} className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs">{role}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Required — high-urgency items for the chargeback processor */}
      {Array.isArray(dashboardData?.actionItems) && dashboardData.actionItems.length > 0 ? (
        <div className="rounded-2xl border border-red-100 bg-red-50/40 p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Action Required</h2>
            <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
              {dashboardData.actionItems.reduce((s, i) => s + (i.count || 0), 0)} items need attention
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {dashboardData.actionItems.map((item) => {
              const sv = ({
                critical: { card: 'border-red-300 hover:border-red-400', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500', count: 'text-red-600' },
                high: { card: 'border-amber-300 hover:border-amber-400', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', count: 'text-amber-600' },
                medium: { card: 'border-orange-200 hover:border-orange-300', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', count: 'text-orange-600' },
                info: { card: 'border-blue-200 hover:border-blue-300', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', count: 'text-blue-600' }
              })[item.severity] || { card: 'border-gray-200', badge: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400', count: 'text-gray-700' };
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.link)}
                  title={item.hint}
                  className={`group flex items-start justify-between gap-3 rounded-xl border bg-white p-4 text-left shadow-sm transition ${sv.card}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${sv.dot}`} />
                      <span className="text-sm font-medium text-gray-900">{item.label}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{item.hint}</p>
                    <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${sv.badge}`}>
                      {item.severity}
                    </span>
                  </div>
                  <span className={`text-2xl font-bold tabular-nums ${sv.count}`}>{item.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-2xl border border-green-100 bg-green-50/50 p-4 text-sm text-green-800 shadow-sm">
          <CheckCircle className="h-5 w-5 text-green-600" />
          You're all caught up — no high-urgency chargeback items right now.
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {stats.map((item) => {
          const soft = {
            'bg-blue-500': 'bg-blue-50 text-blue-600',
            'bg-green-500': 'bg-green-50 text-green-600',
            'bg-indigo-500': 'bg-indigo-50 text-indigo-600',
            'bg-orange-500': 'bg-orange-50 text-orange-600',
            'bg-purple-500': 'bg-purple-50 text-purple-600'
          }[item.color] || 'bg-gray-50 text-gray-600';
          return (
            <div key={item.name} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500">{item.name}</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">{Number(item.value).toLocaleString()}</p>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${soft}`}>
                  <item.icon className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs">
              </div>
            </div>
          );
        })}
      </div>

      {/* Enhanced Analytics Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chargeback Status Chart - All Users */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Chargeback Status</h3>
          <div className="h-64">
            {Object.keys(dashboardData?.statusBreakdown?.chargebacks || {}).length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-500 italic">No chargeback data yet</div>
            ) : (
            <ResponsiveContainer width="100%" height={256}>
              <PieChart>
                <Pie
                  data={Object.entries(dashboardData?.statusBreakdown?.chargebacks || {}).map(([status, count]) => ({
                    name: status,
                    value: count
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  isAnimationActive={false}
                  activeIndex={activeCbSlice}
                  activeShape={renderActiveSlice}
                  onMouseEnter={(_, i) => setActiveCbSlice(i)}
                  onMouseLeave={() => setActiveCbSlice(undefined)}
                >
                  {Object.entries(dashboardData?.statusBreakdown?.chargebacks || {}).map(([status], index) => (
                    <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#EF4444', '#8B5CF6'][index % 4]} />
                  ))}
                </Pie>
                <Tooltip cursor={false} />
              </PieChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Batch Status Chart - Admin & Staff Only */}
        <RoleBasedAccess allowedRoles={['ADMIN', 'STAFF']}>
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Batch Status</h3>
            <div className="h-64">
              {Object.keys(dashboardData?.statusBreakdown?.batches || {}).length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-gray-500 italic">No batch data yet</div>
              ) : (
              <ResponsiveContainer width="100%" height={256}>
                <PieChart>
                  <Pie
                    data={Object.entries(dashboardData?.statusBreakdown?.batches || {}).map(([status, count]) => ({
                      name: status,
                      value: count
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    isAnimationActive={false}
                    activeIndex={activeBatchSlice}
                    activeShape={renderActiveSlice}
                    onMouseEnter={(_, i) => setActiveBatchSlice(i)}
                    onMouseLeave={() => setActiveBatchSlice(undefined)}
                  >
                    {Object.entries(dashboardData?.statusBreakdown?.batches || {}).map(([status], index) => (
                      <Cell key={`cell-${index}`} fill={['#F59E0B', '#10B981', '#EF4444', '#8B5CF6'][index % 4]} />
                    ))}
                  </Pie>
                  <Tooltip cursor={false} />
                </PieChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>
        </RoleBasedAccess>

        {/* System Health - Admin & Staff Only */}
        <RoleBasedAccess allowedRoles={['ADMIN', 'STAFF']}>
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Database</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">API Status</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Online
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Sync</span>
                <span className="text-sm text-gray-900">
                  {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </RoleBasedAccess>
      </div>

    </div>
  );
};

export default Dashboard;
